import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SurveyLogicBuilderStatus } from '@vision/ui/enums';
import {
  SurveyLogicCondition,
  SurveyLogicConditionGroup,
  SurveyLogicRule,
  SurveyLogicRulesConfig,
  Flow,
  SurveyLogicLegacyTrigger,
  SurveyLogicOperator,
  SurveyLogicAction,
  SurveyLogicState,
  SurveyLogicActionType,
  SurveyLogicScope,
} from '@vision/ui/interfaces';
import {
  buildRulesFromFlow,
  validateAllRules,
  SurveyLogicValidationError,
} from '@vision/ui/utils/business/trigger-rule-converter.utils';
import { v4 as uuidv4 } from 'uuid';

export interface SurveyLogicBuilderStateType {
  surveyLogicBuilderStatus: SurveyLogicBuilderStatus;
  flows: Flow[];
  activeFlowId: string;
  surveyStates: SurveyLogicState[];
  rules: SurveyLogicRule[];
  editingRuleId: string | null;
  expandedRuleId: string | null;
  activeLanguageCode: string;
  isDirty: boolean;
  originalConditionRulesConfig: SurveyLogicRulesConfig[] | null;
  validationErrors: SurveyLogicValidationError[];
}

const initialState: SurveyLogicBuilderStateType = {
  surveyLogicBuilderStatus: SurveyLogicBuilderStatus.CLOSED,
  flows: [],
  activeFlowId: null,
  surveyStates: [],
  rules: [],
  editingRuleId: null,
  expandedRuleId: null,
  activeLanguageCode: 'EN',
  isDirty: false,
  originalConditionRulesConfig: null,
  validationErrors: [],
};

const createEmptyCondition = (elementKey?: string): SurveyLogicCondition => ({
  id: uuidv4(),
  controlType: 'option',
  ruleType: 'equals',
  elementKey: elementKey || '',
  optionKey: '',
  optionKeys: [],
  optionKeysOperator: 'and',
  answerValue: '',
  statementKey: '',
  statementKeys: [],
});

const createEmptyConditionGroup = (): SurveyLogicConditionGroup => ({
  id: uuidv4(),
  operator: 'and',
  conditions: [createEmptyCondition()],
});

const createEmptyAction = (): SurveyLogicAction => ({
  type: SurveyLogicActionType.MAKE_VISIBLE,
  scope: SurveyLogicScope.QUESTION,
  targetElementKeys: [],
  targetOptionKeys: [],
  targetEvents: [],
  targetStatementKeys: [],
});

const createEmptyRule = (): SurveyLogicRule => ({
  id: uuidv4(),
  name: '',
  action: createEmptyAction(),
  conditionGroups: [createEmptyConditionGroup()],
  conditionGroupsOperator: 'and',
});

export const SurveyLogicBuilderState = createSlice({
  name: 'SurveyLogicBuilderState',
  initialState,
  reducers: {
    openSurveyLogicBuilderDrawer: (state) => {
      state.surveyLogicBuilderStatus = SurveyLogicBuilderStatus.OPENED;
    },

    closeSurveyLogicBuilderDrawer: (state) => {
      state.surveyLogicBuilderStatus = SurveyLogicBuilderStatus.CLOSED;
      state.editingRuleId = null;
    },

    setActiveFlow: (
      state,
      action: PayloadAction<{
        flowId: string;
        states: SurveyLogicState[];
        rules: SurveyLogicRule[];
      }>,
    ) => {
      state.activeFlowId = action.payload.flowId;
      state.surveyStates = action.payload.states;
      state.rules = action.payload.rules;
      state.isDirty = false;
    },

    loadFlowData: (
      state,
      action: PayloadAction<{
        flowId: string;
        states: SurveyLogicState[];
        conditionRulesConfig?: SurveyLogicRulesConfig[];
        activeLanguageCode?: string;
      }>,
    ) => {
      const { flowId, states, conditionRulesConfig, activeLanguageCode } = action.payload;
      const rules = buildRulesFromFlow(conditionRulesConfig);

      state.activeFlowId = flowId;
      state.surveyStates = states;
      state.rules = rules;
      state.originalConditionRulesConfig = conditionRulesConfig || null;
      state.activeLanguageCode = activeLanguageCode || 'EN';
      state.isDirty = false;
      state.validationErrors = [];
    },

    setActiveLanguageCode: (state, action: PayloadAction<string>) => {
      state.activeLanguageCode = action.payload;
    },

    addRule: (state) => {
      const newRule = createEmptyRule();
      state.rules.push(newRule);
      state.editingRuleId = newRule.id;
      state.expandedRuleId = newRule.id;
      state.isDirty = true;
      state.validationErrors = [];
    },

    removeRule: (state, action: PayloadAction<string>) => {
      state.rules = state.rules.filter((rule) => rule.id !== action.payload);
      if (state.editingRuleId === action.payload) {
        state.editingRuleId = null;
      }
      state.isDirty = true;
      state.validationErrors = [];
    },

    setEditingRule: (state, action: PayloadAction<string | null>) => {
      state.editingRuleId = action.payload;
    },

    setExpandedRuleId: (state, action: PayloadAction<string | null>) => {
      state.expandedRuleId = action.payload;
    },

    toggleExpandedRuleId: (state, action: PayloadAction<string>) => {
      state.expandedRuleId = state.expandedRuleId === action.payload ? null : action.payload;
    },

    updateRuleName: (state, action: PayloadAction<{ ruleId: string; name: string }>) => {
      const rule = state.rules.find((r) => r.id === action.payload.ruleId);
      if (rule) {
        rule.name = action.payload.name;
        state.isDirty = true;
        state.validationErrors = [];
      }
    },

    updateRuleAction: (state, action: PayloadAction<{ ruleId: string; ruleAction: SurveyLogicAction }>) => {
      const rule = state.rules.find((r) => r.id === action.payload.ruleId);
      if (rule) {
        rule.action = action.payload.ruleAction;
        state.isDirty = true;
        state.validationErrors = [];
      }
    },

    updateConditionGroupsOperator: (
      state,
      action: PayloadAction<{ ruleId: string; operator: SurveyLogicOperator }>,
    ) => {
      const rule = state.rules.find((r) => r.id === action.payload.ruleId);
      if (rule) {
        rule.conditionGroupsOperator = action.payload.operator;
        state.isDirty = true;
        state.validationErrors = [];
      }
    },

    addConditionGroup: (state, action: PayloadAction<string>) => {
      const rule = state.rules.find((r) => r.id === action.payload);
      if (rule) {
        rule.conditionGroups.push(createEmptyConditionGroup());
        state.isDirty = true;
        state.validationErrors = [];
      }
    },

    removeConditionGroup: (state, action: PayloadAction<{ ruleId: string; groupId: string }>) => {
      const rule = state.rules.find((r) => r.id === action.payload.ruleId);
      if (rule && rule.conditionGroups.length > 1) {
        rule.conditionGroups = rule.conditionGroups.filter((g) => g.id !== action.payload.groupId);
        state.isDirty = true;
        state.validationErrors = [];
      }
    },

    updateConditionGroupOperator: (
      state,
      action: PayloadAction<{ ruleId: string; groupId: string; operator: SurveyLogicOperator }>,
    ) => {
      const rule = state.rules.find((r) => r.id === action.payload.ruleId);
      if (rule) {
        const group = rule.conditionGroups.find((g) => g.id === action.payload.groupId);
        if (group) {
          group.operator = action.payload.operator;
          state.isDirty = true;
          state.validationErrors = [];
        }
      }
    },

    addCondition: (state, action: PayloadAction<{ ruleId: string; groupId: string }>) => {
      const rule = state.rules.find((r) => r.id === action.payload.ruleId);
      if (rule) {
        const group = rule.conditionGroups.find((g) => g.id === action.payload.groupId);
        if (group) {
          group.conditions.push(createEmptyCondition());
          state.isDirty = true;
          state.validationErrors = [];
        }
      }
    },

    removeRuleCondition: (state, action: PayloadAction<{ ruleId: string; groupId: string; conditionId: string }>) => {
      const rule = state.rules.find((r) => r.id === action.payload.ruleId);
      if (rule) {
        const group = rule.conditionGroups.find((g) => g.id === action.payload.groupId);
        if (group && group.conditions.length > 1) {
          group.conditions = group.conditions.filter((c) => c.id !== action.payload.conditionId);
          state.isDirty = true;
          state.validationErrors = [];
        }
      }
    },

    updateCondition: (
      state,
      action: PayloadAction<{
        ruleId: string;
        groupId: string;
        conditionId: string;
        updates: Partial<SurveyLogicCondition>;
      }>,
    ) => {
      const rule = state.rules.find((r) => r.id === action.payload.ruleId);
      if (rule) {
        const group = rule.conditionGroups.find((g) => g.id === action.payload.groupId);
        if (group) {
          const condition = group.conditions.find((c) => c.id === action.payload.conditionId);
          if (condition) {
            Object.assign(condition, action.payload.updates);
            state.isDirty = true;
            state.validationErrors = [];
          }
        }
      }
    },

    resetDirtyState: (state) => {
      state.isDirty = false;
      state.expandedRuleId = null;
    },

    clearSurveyLogicBuilderState: (state) => {
      state.activeFlowId = null;
      state.surveyStates = [];
      state.rules = [];
      state.editingRuleId = null;
      state.expandedRuleId = null;
      state.isDirty = false;
      state.validationErrors = [];
      state.originalConditionRulesConfig = null;
    },

    validateRules: (state) => {
      const { errors } = validateAllRules(state.rules);
      state.validationErrors = errors;
    },

    duplicateRule: (state, action: PayloadAction<string>) => {
      const ruleToDuplicate = state.rules.find((r) => r.id === action.payload);
      if (ruleToDuplicate) {
        const newRule: SurveyLogicRule = {
          ...ruleToDuplicate,
          id: uuidv4(),
          name: ruleToDuplicate.name ? `${ruleToDuplicate.name} (Copy)` : '',
          conditionGroups: ruleToDuplicate.conditionGroups.map((group) => ({
            ...group,
            id: uuidv4(),
            conditions: group.conditions.map((condition) => ({
              ...condition,
              id: uuidv4(),
            })),
          })),
        };
        state.rules.push(newRule);
        state.isDirty = true;
        state.validationErrors = [];
      }
    },

    reorderRules: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
      const { fromIndex, toIndex } = action.payload;
      const [removed] = state.rules.splice(fromIndex, 1);
      state.rules.splice(toIndex, 0, removed);
      state.isDirty = true;
      state.validationErrors = [];
    },

    createRuleFromTrigger: (
      state,
      action: PayloadAction<{
        elementKey: string;
        trigger: SurveyLogicLegacyTrigger;
        triggerIndex: number;
      }>,
    ) => {
      const { elementKey, trigger, triggerIndex } = action.payload;
      const condition: SurveyLogicCondition = {
        id: uuidv4(),
        controlType: trigger.controlType,
        ruleType: trigger.ruleType || 'equals',
        elementKey,
        optionKey: trigger.optionKey || '',
        answerValue: trigger.answerValue || '',
        statementKey: trigger.statementKey || '',
        triggerRef: {
          elementKey,
          triggerIndex,
        },
      };
      const conditionGroup: SurveyLogicConditionGroup = {
        id: uuidv4(),
        operator: 'and',
        conditions: [condition],
      };
      const ruleAction: SurveyLogicAction = {
        type: trigger.action,
        scope: trigger.scope,
        targetElementKeys: trigger.targetElementKey || [],
        targetOptionKeys: trigger.targetOptionKeys || [],
        targetEvents: trigger.targetEvents || [],
        targetStatementKeys: trigger.targetStatementKeys || [],
      };
      const newRule: SurveyLogicRule = {
        id: trigger.id || uuidv4(),
        name: '',
        action: ruleAction,
        conditionGroups: [conditionGroup],
        conditionGroupsOperator: 'and',
      };

      state.rules.push(newRule);
      state.editingRuleId = newRule.id;
      state.isDirty = true;
      state.validationErrors = [];
    },

    addConditionFromTrigger: (
      state,
      action: PayloadAction<{
        ruleId: string;
        groupId: string;
        elementKey: string;
        trigger: SurveyLogicLegacyTrigger;
        triggerIndex: number;
      }>,
    ) => {
      const { ruleId, groupId, elementKey, trigger, triggerIndex } = action.payload;
      const rule = state.rules.find((r) => r.id === ruleId);

      if (rule) {
        const group = rule.conditionGroups.find((g) => g.id === groupId);
        if (group) {
          const condition: SurveyLogicCondition = {
            id: uuidv4(),
            controlType: trigger.controlType,
            ruleType: trigger.ruleType || 'equals',
            elementKey,
            optionKey: trigger.optionKey || '',
            answerValue: trigger.answerValue || '',
            statementKey: trigger.statementKey || '',
            triggerRef: {
              elementKey,
              triggerIndex,
            },
          };

          group.conditions.push(condition);
          state.isDirty = true;
          state.validationErrors = [];
        }
      }
    },

    prepareForSave: (state) => {
      const { errors } = validateAllRules(state.rules);
      state.validationErrors = errors;
    },
  },
});

export const {
  closeSurveyLogicBuilderDrawer,
  openSurveyLogicBuilderDrawer,
  setActiveFlow,
  loadFlowData,
  setActiveLanguageCode,
  addRule,
  removeRule,
  setEditingRule,
  setExpandedRuleId,
  toggleExpandedRuleId,
  updateRuleName,
  updateRuleAction,
  updateConditionGroupsOperator,
  addConditionGroup,
  removeConditionGroup,
  updateConditionGroupOperator,
  addCondition,
  removeRuleCondition,
  updateCondition,
  resetDirtyState,
  clearSurveyLogicBuilderState,
  validateRules,
  duplicateRule,
  reorderRules,
  createRuleFromTrigger,
  addConditionFromTrigger,
  prepareForSave,
} = SurveyLogicBuilderState.actions;

export {
  rulesToConditionRulesConfig,
  validateAllRules,
  type SurveyLogicValidationError,
} from '@vision/ui/utils/business/trigger-rule-converter.utils';
